import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TableOfContents from './components/TableOfContents';
import RelatedArticles from './components/RelatedArticles';
import AuthorBio from './components/AuthorBio';
import SocialShare from './components/SocialShare';
import QuickTakeaways from './components/QuickTakeaways';
import CommentSection from './components/CommentSection';

const ArticleDetailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);

  const slug = searchParams.get('slug');

  // Mock article data with full GEO optimization
  const mockArticle = {
    id: 1,
    title: "Gift Ideas for 16-Year-Old Gaming Enthusiasts: Ultimate Guide 2024",
    slug: "gift-ideas-16-year-old-gaming-enthusiasts-2024",
    excerpt: "Discover the perfect gifts for teenage gamers with our comprehensive guide featuring the latest gaming accessories, tech gadgets, and unique items that will level up their gaming experience.",
    author: {
      name: "Sarah Johnson",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      credentials: "Gaming Industry Specialist, 8+ years experience",
      bio: "Sarah has been covering gaming trends and technology for over 8 years. She's helped thousands of parents and gift-givers find the perfect presents for gaming enthusiasts.",
      isVerified: true,
      socialLinks: {
        twitter: "@sarahjohnson",
        linkedin: "sarah-johnson-gaming"
      }
    },
    category: "Age Groups",
    subcategory: "Teens",
    tags: ["16-year-old", "gaming", "teenagers", "tech", "accessories", "2024"],
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&h=400&fit=crop",
    publishedAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-20'),
    readTime: 12,
    views: 15847,
    shares: 423,
    likes: 1267,
    difficulty: "Easy",
    budgetRange: "$25 - $200",
    occasionTags: ["birthday", "holiday", "just-because"],
    
    // GEO optimized content structure
    tableOfContents: [
      { id: 'introduction', title: 'Why Gaming Gifts Matter for Teens', level: 2 },
      { id: 'gaming-accessories', title: 'Essential Gaming Accessories', level: 2 },
      { id: 'peripherals', title: 'Gaming Peripherals That Make a Difference', level: 2 },
      { id: 'tech-gadgets', title: 'Tech Gadgets Beyond Gaming', level: 2 },
      { id: 'unique-finds', title: 'Unique Gaming-Themed Gifts', level: 2 },
      { id: 'budget-guide', title: 'Gift Ideas by Budget Range', level: 2 },
      { id: 'expert-tips', title: 'Expert Tips for Gift Selection', level: 2 },
      { id: 'conclusion', title: 'Final Recommendations', level: 2 }
    ],

    quickTakeaways: [
      "Gaming headsets are the #1 most appreciated gift by 16-year-old gamers",
      "Budget range of $50-$100 offers the best value for gaming accessories",
      "RGB lighting and customization features are highly valued by teens",
      "Consider the gaming platform (PC, PlayStation, Xbox, Nintendo) before purchasing",
      "Gift cards to gaming platforms are always a safe backup option"
    ],

    expertQuotes: [
      {
        expert: "Dr. Emily Chen, Child Development Specialist",
        quote: "Gaming can enhance problem-solving skills and creativity in teenagers when balanced appropriately with other activities.",
        credentials: "PhD in Child Psychology, 15+ years experience"
      },
      {
        expert: "Marcus Rodriguez, Gaming Industry Analyst",
        quote: "The gaming accessories market has grown 300% in the past 5 years, with teens being the primary demographic driving innovation.",
        credentials: "Senior Analyst at GameTech Research"
      }
    ],

    citations: [
      {
        title: "Gaming Industry Report 2024",
        source: "TechStats Research",
        url: "https://example.com/gaming-report-2024"
      },
      {
        title: "Teenage Gift Preferences Study",
        source: "Consumer Insights Magazine",
        url: "https://example.com/teen-gift-study"
      }
    ],

    // Structured content sections
    sections: [
      {
        id: 'introduction',
        title: 'Why Gaming Gifts Matter for Teens',
        content: `
          <p>For 16-year-old gaming enthusiasts, the right gift can transform their entire gaming experience. According to recent studies, <strong>78% of teenagers</strong> consider gaming their primary hobby, making gaming-related gifts highly appreciated and frequently used.</p>
          
          <p>The gaming industry has evolved significantly, with accessories and peripherals now playing crucial roles in competitive gaming and overall enjoyment. When selecting gifts for teenage gamers, consider these key factors:</p>
          
          <ul>
            <li><strong>Platform compatibility</strong> - Ensure gifts work with their gaming system</li>
            <li><strong>Build quality</strong> - Teenagers use their gaming equipment extensively</li>
            <li><strong>Aesthetic appeal</strong> - RGB lighting and customization options are highly valued</li>
            <li><strong>Functionality</strong> - Features that enhance gameplay or comfort</li>
          </ul>
        `
      },
      {
        id: 'gaming-accessories',
        title: 'Essential Gaming Accessories',
        content: `
          <h3>1. Gaming Headsets ($40-$150)</h3>
          <p>A quality gaming headset is often considered the most important accessory for serious gamers. Here's what to look for:</p>
          
          <ul>
            <li><strong>Audio quality</strong> - Clear directional audio for competitive advantage</li>
            <li><strong>Microphone</strong> - Essential for team communication</li>
            <li><strong>Comfort</strong> - Padded ear cups for long gaming sessions</li>
            <li><strong>Noise isolation</strong> - Reduces external distractions</li>
          </ul>
          
          <h4>Top Recommendations:</h4>
          <ul>
            <li>SteelSeries Arctis 7P ($150) - Premium wireless option</li>
            <li>HyperX Cloud Alpha ($100) - Best value for money</li>
            <li>Razer BlackShark V2 ($100) - Excellent for FPS games</li>
          </ul>
          
          <h3>2. Gaming Keyboards ($60-$200)</h3>
          <p>Mechanical keyboards can significantly improve gaming performance and typing experience:</p>
          
          <ul>
            <li><strong>Switch types</strong> - Cherry MX Red for gaming, Blue for typing</li>
            <li><strong>RGB lighting</strong> - Customizable backlighting effects</li>
            <li><strong>Macro keys</strong> - Programmable shortcuts for complex games</li>
            <li><strong>Build quality</strong> - Durable construction for heavy use</li>
          </ul>
        `
      },
      {
        id: 'peripherals',title: 'Gaming Peripherals That Make a Difference',
        content: `
          <h3>Gaming Mice ($30-$120)</h3>
          <p>A responsive gaming mouse is crucial for competitive gaming, especially in FPS and strategy games:</p>
          
          <ul>
            <li><strong>DPI range</strong> - Higher DPI for faster cursor movement</li>
            <li><strong>Polling rate</strong> - 1000Hz for professional-level responsiveness</li>
            <li><strong>Ergonomics</strong> - Comfortable grip for extended use</li>
            <li><strong>Programmable buttons</strong> - Customizable controls for different games</li>
          </ul>
          
          <h3>Mouse Pads and Surfaces ($20-$80)</h3>
          <p>Often overlooked but essential for consistent mouse performance:</p>
          
          <ul>
            <li><strong>Size</strong> - Large surfaces for low-sensitivity gaming</li>
            <li><strong>Material</strong> - Cloth for control, hard surfaces for speed</li>
            <li><strong>RGB integration</strong> - Matches other gaming peripherals</li>
            <li><strong>Stitched edges</strong> - Prevents fraying with heavy use</li>
          </ul>
        `
      },
      {
        id: 'tech-gadgets',title: 'Tech Gadgets Beyond Gaming',
        content: `
          <h3>Streaming and Content Creation ($50-$300)</h3>
          <p>Many teenage gamers are interested in streaming or creating content:</p>
          
          <ul>
            <li><strong>Webcams</strong> - Logitech C920 ($70) for clear streaming</li>
            <li><strong>Microphones</strong> - Blue Yeti ($100) for professional audio</li>
            <li><strong>Lighting kits</strong> - Ring lights for better video quality</li>
            <li><strong>Capture cards</strong> - Elgato HD60 S ($150) for console streaming</li>
          </ul>
          
          <h3>Gaming Chairs ($100-$400)</h3>
          <p>Ergonomic seating for long gaming sessions:</p>
          
          <ul>
            <li><strong>Lumbar support</strong> - Prevents back pain during extended play</li>
            <li><strong>Adjustability</strong> - Height, armrests, and tilt options</li>
            <li><strong>Material quality</strong> - Breathable fabric or easy-clean leather</li>
            <li><strong>Weight capacity</strong> - Suitable for growing teenagers</li>
          </ul>
        `
      },
      {
        id: 'unique-finds',title: 'Unique Gaming-Themed Gifts',
        content: `
          <h3>Collectibles and Memorabilia ($15-$150)</h3>
          <p>For gamers who love their favorite franchises:</p>
          
          <ul>
            <li><strong>Action figures</strong> - High-quality collectibles from popular games</li>
            <li><strong>Posters and artwork</strong> - Gaming-themed wall decorations</li>
            <li><strong>Gaming-themed apparel</strong> - T-shirts, hoodies, and accessories</li>
            <li><strong>Limited edition items</strong> - Special releases from favorite games</li>
          </ul>
          
          <h3>DIY and Customization ($25-$100)</h3>
          <p>Gifts that let them personalize their gaming setup:</p>
          
          <ul>
            <li><strong>Custom keycaps</strong> - Personalized keyboard modifications</li>
            <li><strong>Cable management</strong> - Organize their gaming setup</li>
            <li><strong>LED strip lights</strong> - Ambient lighting for their gaming space</li>
            <li><strong>Desk organizers</strong> - Gaming-themed storage solutions</li>
          </ul>
        `
      },
      {
        id: 'budget-guide',title: 'Gift Ideas by Budget Range',
        content: `
          <h3>Under $25</h3>
          <ul>
            <li>Gaming mouse pad with RGB lighting</li>
            <li>Custom gaming keycaps set</li>
            <li>Gaming-themed phone case</li>
            <li>LED strip lights for gaming setup</li>
            <li>Gaming cable organizer kit</li>
          </ul>
          
          <h3>$25-$50</h3>
          <ul>
            <li>Quality gaming mouse</li>
            <li>Gaming headset stand with USB hub</li>
            <li>Collectible gaming figures</li>
            <li>Gaming-themed apparel</li>
            <li>Mechanical keyboard switches sampler</li>
          </ul>
          
          <h3>$50-$100</h3>
          <ul>
            <li>Entry-level gaming headset</li>
            <li>Gaming keyboard with basic features</li>
            <li>Streaming webcam</li>
            <li>Gaming chair cushion set</li>
            <li>Game-specific collectibles</li>
          </ul>
          
          <h3>$100-$200</h3>
          <ul>
            <li>High-quality gaming headset</li>
            <li>Mechanical gaming keyboard</li>
            <li>Professional gaming mouse</li>
            <li>Streaming microphone</li>
            <li>Gaming monitor upgrade</li>
          </ul>
          
          <h3>Over $200</h3>
          <ul>
            <li>Premium gaming chair</li>
            <li>High-end gaming keyboard and mouse combo</li>
            <li>Professional streaming setup</li>
            <li>Gaming laptop accessories bundle</li>
            <li>VR gaming accessories</li>
          </ul>
        `
      },
      {
        id: 'expert-tips',title: 'Expert Tips for Gift Selection',
        content: `
          <h3>Research Their Current Setup</h3>
          <p>Before purchasing any gaming gift, it's essential to understand what they already have:</p>
          
          <ul>
            <li><strong>Gaming platform</strong> - PC, PlayStation, Xbox, Nintendo Switch</li>
            <li><strong>Current peripherals</strong> - What they're using now and what needs upgrading</li>
            <li><strong>Gaming preferences</strong> - FPS, RPG, strategy games have different accessory needs</li>
            <li><strong>Space constraints</strong> - Room size and desk space available</li>
          </ul>
          
          <h3>Consider Future Compatibility</h3>
          <p>Technology evolves rapidly, so consider gifts that will remain relevant:</p>
          
          <ul>
            <li><strong>USB-C connectivity</strong> - Future-proof connection standard</li>
            <li><strong>Wireless options</strong> - Reduces cable clutter and increases flexibility</li>
            <li><strong>Platform agnostic</strong> - Works across multiple gaming systems</li>
            <li><strong>Modular designs</strong> - Can be upgraded or customized over time</li>
          </ul>
          
          <h3>Quality Over Quantity</h3>
          <p>It's better to invest in one high-quality item than multiple mediocre ones:</p>
          
          <ul>
            <li><strong>Brand reputation</strong> - Established gaming brands offer better support</li>
            <li><strong>Warranty coverage</strong> - Protection for their investment</li>
            <li><strong>User reviews</strong> - Real-world performance feedback</li>
            <li><strong>Longevity</strong> - Items that will last through heavy teenage use</li>
          </ul>
        `
      },
      {
        id: 'conclusion',
        title: 'Final Recommendations',
        content: `
          <p>Selecting the perfect gift for a 16-year-old gaming enthusiast requires understanding their preferences, current setup, and gaming habits. The most successful gifts often fall into these categories:</p>
          
          <h3>Top Gift Categories (In Order of Preference)</h3>
          <ol>
            <li><strong>Gaming Headsets</strong> - Essential for communication and immersion</li>
            <li><strong>Gaming Keyboards</strong> - Improve performance and typing experience</li>
            <li><strong>Gaming Mice</strong> - Crucial for competitive gaming</li>
            <li><strong>Gaming Chairs</strong> - Comfort for long gaming sessions</li>
            <li><strong>Streaming Equipment</strong> - For content creation aspirations</li>
          </ol>
          
          <h3>Safe Backup Options</h3>
          <p>If you're unsure about specific hardware preferences:</p>
          
          <ul>
            <li><strong>Gaming platform gift cards</strong> - Steam, PlayStation Store, Xbox Live</li>
            <li><strong>Gaming merchandise</strong> - From their favorite game franchises</li>
            <li><strong>Customization accessories</strong> - LED lights, cable management</li>
            <li><strong>Gaming-themed room decor</strong> - Posters, lighting, organization</li>
          </ul>
          
          <p>Remember that the best gifts show you understand and support their interests. Gaming is often a social activity for teenagers, so gifts that enhance their ability to connect with friends and enjoy their hobby are always appreciated.</p>
        `
      }
    ],

    relatedArticles: [
      {
        id: 2,
        title: "Budget Gaming Setup Guide for Teens",
        slug: "budget-gaming-setup-guide-teens",
        image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=200&fit=crop",
        readTime: 8
      },
      {
        id: 3,
        title: "Best Gaming Gifts Under $50",
        slug: "best-gaming-gifts-under-50",
        image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=300&h=200&fit=crop",
        readTime: 6
      },
      {
        id: 4,
        title: "Gaming Room Setup Ideas for Teenagers",
        slug: "gaming-room-setup-ideas-teenagers",
        image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=300&h=200&fit=crop",
        readTime: 10
      }
    ]
  };

  useEffect(() => {
    // Simulate loading article data
    setLoading(true);
    setTimeout(() => {
      setArticle(mockArticle);
      setLoading(false);
    }, 1000);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.body.scrollHeight - window.innerHeight;
      const progress = (scrolled / maxScroll) * 100;
      setReadingProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleShare = (platform) => {
    console.log(`Sharing on ${platform}:`, article?.title);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-accent-600">Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="FileText" size={64} className="text-accent-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-accent-800 mb-2">Article Not Found</h2>
            <p className="text-accent-600 mb-6">The article you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/gift-ideas-hub')}>
              Browse All Articles
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "author": {
      "@type": "Person",
      "name": article.author.name
    },
    "publisher": {
      "@type": "Organization",
      "name": "Giftsy Modern",
      "logo": {
        "@type": "ImageObject",
        "url": "https://giftsy.com/assets/images/Giftsy-Logo-Final-1751671984839.png"
      }
    },
    "datePublished": article.publishedAt.toISOString(),
    "dateModified": article.updatedAt.toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://giftsy.com/article-detail-page?slug=${article.slug}`
    }
  };

  return (
    <>
      <Helmet>
        <title>{article.title} | Giftsy Modern</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={article.tags.join(', ')} />
        <meta name="author" content={article.author.name} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={article.image} />
        <meta property="og:url" content={`https://giftsy.com/article-detail-page?slug=${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="article:author" content={article.author.name} />
        <meta property="article:published_time" content={article.publishedAt.toISOString()} />
        <meta property="article:modified_time" content={article.updatedAt.toISOString()} />
        <meta property="article:tag" content={article.tags.join(', ')} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={article.image} />
        <link rel="canonical" href={`https://giftsy.com/article-detail-page?slug=${article.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-surface">
        <Header />
        
        {/* Reading Progress Bar */}
        <div className="fixed top-0 left-0 right-0 h-1 bg-accent-100 z-40">
          <div 
            className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
            style={{ width: `${readingProgress}%` }}
          />
        </div>

        <article className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Article Header */}
            <header className="mb-8">
              {/* Breadcrumb */}
              <nav className="flex items-center space-x-2 text-sm text-accent-600 mb-6">
                <button onClick={() => navigate('/gift-ideas-hub')} className="hover:text-primary">
                  Gift Ideas Hub
                </button>
                <Icon name="ChevronRight" size={16} />
                <span>{article.category}</span>
                <Icon name="ChevronRight" size={16} />
                <span className="text-accent-800">{article.title}</span>
              </nav>

              {/* Category and Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                  {article.category}
                </span>
                {article.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="bg-accent-100 text-accent-700 px-3 py-1 rounded-full text-sm">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Title */}
              <h1 className="font-heading font-bold text-3xl lg:text-4xl text-accent-800 mb-6 leading-tight">
                {article.title}
              </h1>

              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-accent-600">
                <div className="flex items-center space-x-2">
                  <img
                    src={article.author.avatar}
                    alt={article.author.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-accent-800">{article.author.name}</span>
                      {article.author.isVerified && (
                        <Icon name="CheckCircle" size={16} className="text-primary" />
                      )}
                    </div>
                    <span className="text-xs">{article.author.credentials}</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={16} />
                  <span>{article.publishedAt.toLocaleDateString()}</span>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={16} />
                  <span>{article.readTime} min read</span>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Icon name="Eye" size={16} />
                  <span>{article.views.toLocaleString()} views</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between mb-8">
                <SocialShare 
                  article={article}
                  onShare={handleShare}
                />
                
                <Button
                  variant={isBookmarked ? "primary" : "outline"}
                  onClick={handleBookmark}
                  iconName={isBookmarked ? "BookmarkCheck" : "Bookmark"}
                  iconPosition="left"
                  size="sm"
                >
                  {isBookmarked ? "Bookmarked" : "Bookmark"}
                </Button>
              </div>

              {/* Featured Image */}
              <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-64 lg:h-80 object-cover"
                />
              </div>
            </header>

            {/* Main Content Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sidebar */}
              <aside className="lg:col-span-1 space-y-6">
                <TableOfContents sections={article.tableOfContents} />
                <QuickTakeaways takeaways={article.quickTakeaways} />
              </aside>

              {/* Article Content */}
              <main className="lg:col-span-3">
                <div className="prose prose-lg max-w-none">
                  {article.sections.map((section) => (
                    <section key={section.id} id={section.id} className="mb-12">
                      <h2 className="font-heading font-bold text-2xl text-accent-800 mb-6">
                        {section.title}
                      </h2>
                      <div 
                        className="text-accent-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      />
                    </section>
                  ))}
                </div>

                {/* Expert Quotes */}
                <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 my-12 border border-primary-100">
                  <h3 className="font-heading font-bold text-xl text-accent-800 mb-6">
                    Expert Insights
                  </h3>
                  <div className="space-y-6">
                    {article.expertQuotes.map((quote, index) => (
                      <blockquote key={index} className="border-l-4 border-primary pl-6">
                        <p className="text-lg italic text-accent-700 mb-3">
                          "{quote.quote}"
                        </p>
                        <cite className="text-sm text-accent-600">
                          — {quote.expert}, {quote.credentials}
                        </cite>
                      </blockquote>
                    ))}
                  </div>
                </div>

                {/* Citations */}
                <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6 my-12">
                  <h3 className="font-heading font-semibold text-lg text-accent-800 mb-4">
                    References & Sources
                  </h3>
                  <ol className="space-y-2 text-sm text-accent-600">
                    {article.citations.map((citation, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="font-medium text-accent-800">{index + 1}.</span>
                        <div>
                          <a 
                            href={citation.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {citation.title}
                          </a>
                          <span className="text-accent-500"> - {citation.source}</span>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Author Bio */}
                <AuthorBio author={article.author} />

                {/* Related Articles */}
                <RelatedArticles articles={article.relatedArticles} />

                {/* Comments */}
                <CommentSection articleId={article.id} />
              </main>
            </div>
          </div>
        </article>
      </div>
    </>
  );
};

export default ArticleDetailPage;